import React, { useEffect, useState } from 'react';
import { api, API_ORIGIN } from '../../api.js';
import ManagedDropdown from './ManagedDropdown.jsx';
import FileThumbPicker from './FileThumbPicker.jsx';

const CATEGORIES = [
  { key: 'travel', label: 'Travel Docs' },
  { key: 'health', label: 'Health Docs' },
  { key: 'disciplinary', label: 'Disciplinary Measures' },
  { key: 'advance_payment', label: 'Advance Payments' },
  { key: 'tickets_accidents', label: 'Tickets & Accidents' }
];

export default function DriverDocumentsTab({ driverId }) {
  const [category, setCategory] = useState('travel');
  const [entries, setEntries] = useState({});
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);

  const load = (cat) => {
    api(`/dispatch/drivers/${driverId}/documents?category=${cat}`)
      .then(rows => setEntries(e => ({ ...e, [cat]: rows })))
      .catch(() => setEntries(e => ({ ...e, [cat]: [] })));
  };
  useEffect(() => { load(category); }, [category]);

  const deleteEntry = async (id) => {
    await api(`/dispatch/drivers/${driverId}/documents/${id}`, { method: 'DELETE' });
    load(category);
  };

  const rows = entries[category] || [];

  return (
    <div>
      <div className="driver-subtabs">
        {CATEGORIES.map(c => (
          <button key={c.key} type="button" className={'driver-subtab' + (category === c.key ? ' active' : '')}
            onClick={() => setCategory(c.key)}>{c.label}</button>
        ))}
      </div>

      {(category === 'travel' || category === 'health') && (
        <TravelHealthForm driverId={driverId} category={category} rows={rows} onSaved={() => load(category)} onDelete={deleteEntry} />
      )}
      {category === 'disciplinary' && (
        <DisciplinaryForm driverId={driverId} rows={rows} onSaved={() => load(category)} onDelete={deleteEntry} />
      )}
      {category === 'advance_payment' && (
        <AdvancePaymentsPanel driverId={driverId} rows={rows} onSaved={() => load(category)} onDelete={deleteEntry}
          modalOpen={advanceModalOpen} setModalOpen={setAdvanceModalOpen} />
      )}
      {category === 'tickets_accidents' && (
        <TicketsAccidentsForm driverId={driverId} rows={rows} onSaved={() => load(category)} onDelete={deleteEntry} />
      )}
    </div>
  );
}

function TravelHealthForm({ driverId, category, rows, onSaved, onDelete }) {
  const [description, setDescription] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [authority, setAuthority] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);

  const descPlaceholder = category === 'health'
    ? 'e.g. Health Card, Medical Exam, DOT Physical'
    : 'e.g. Passport, Visa, FAST Card';

  const add = async () => {
    if (!description.trim()) return;
    await api(`/dispatch/drivers/${driverId}/documents`, {
      method: 'POST',
      body: {
        category, description, documentNumber, authority, issueDate, expiryDate,
        fileName: uploadedFile?.fileName || '', fileUrl: uploadedFile?.url || ''
      }
    });
    setDescription(''); setDocumentNumber(''); setAuthority(''); setIssueDate(''); setExpiryDate(''); setUploadedFile(null);
    onSaved();
  };

  return (
    <div>
      <label>Description<br/>
        <input value={description} onChange={e => setDescription(e.target.value)} placeholder={descPlaceholder} />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, margin: '14px 0 14px' }}>
        <label>Document Number<br/><input value={documentNumber} onChange={e => setDocumentNumber(e.target.value)} /></label>
        <label>Authority<br/><input value={authority} onChange={e => setAuthority(e.target.value)} /></label>
        <label>Issue Date<br/><input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} /></label>
        <label>Expiry Date<br/><input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} /></label>
      </div>
      <div style={{ marginBottom: 18 }}>
        <FileThumbPicker value={uploadedFile} onChange={setUploadedFile} />
      </div>
      <button type="button" className="primary-btn" onClick={add} style={{ marginBottom: 18 }}>+ Add</button>

      {rows.length === 0 ? <div className="empty">No entries yet.</div> : (
        <table>
          <thead><tr><th>Description</th><th>Doc #</th><th>Authority</th><th>Issue</th><th>Expiry</th><th>File</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.description || '—'}</td>
                <td>{r.document_number || '—'}</td>
                <td>{r.authority || '—'}</td>
                <td>{r.issue_date || '—'}</td>
                <td>{r.expiry_date || '—'}</td>
                <td>{r.file_url ? <a href={`${API_ORIGIN}${r.file_url}`} target="_blank" rel="noreferrer">{r.file_name || 'View'}</a> : '—'}</td>
                <td><button type="button" onClick={() => onDelete(r.id)} style={{ color: 'var(--red)' }}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DisciplinaryForm({ driverId, rows, onSaved, onDelete }) {
  const [driverAction, setDriverAction] = useState('');
  const [measure, setMeasure] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);

  const add = async () => {
    if (!driverAction && !measure && !notes.trim()) return;
    await api(`/dispatch/drivers/${driverId}/documents`, {
      method: 'POST',
      body: {
        category: 'disciplinary', entryDate: date, driverAction, measure, description: notes,
        fileName: uploadedFile?.fileName || '', fileUrl: uploadedFile?.url || ''
      }
    });
    setDriverAction(''); setMeasure(''); setNotes(''); setDate(''); setUploadedFile(null);
    onSaved();
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <label>Driver Action<br/>
          <ManagedDropdown field="driver_action" value={driverAction} onChange={setDriverAction} />
        </label>
        <label>Disciplinary Measure<br/>
          <ManagedDropdown field="disciplinary_measure" value={measure} onChange={setMeasure} />
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 14 }}>
        <label>Notes<br/><input value={notes} onChange={e => setNotes(e.target.value)} /></label>
        <label>Date<br/><input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
      </div>
      <div style={{ marginBottom: 18 }}>
        <FileThumbPicker value={uploadedFile} onChange={setUploadedFile} label="+ Add reprimand file (JPEG/PDF)" />
      </div>
      <button type="button" className="primary-btn" onClick={add} style={{ marginBottom: 18 }}>+ Add</button>

      {rows.length === 0 ? <div className="empty">No entries yet.</div> : (
        <table>
          <thead><tr><th>Date</th><th>Driver Action</th><th>Measure</th><th>Notes</th><th>File</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.entry_date || '—'}</td>
                <td>{r.driver_action || '—'}</td>
                <td>{r.measure || '—'}</td>
                <td>{r.description || '—'}</td>
                <td>{r.file_url ? <a href={`${API_ORIGIN}${r.file_url}`} target="_blank" rel="noreferrer">{r.file_name || 'View'}</a> : '—'}</td>
                <td><button type="button" onClick={() => onDelete(r.id)} style={{ color: 'var(--red)' }}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AdvancePaymentsPanel({ driverId, rows, onSaved, onDelete, modalOpen, setModalOpen }) {
  const total = rows.reduce((sum, r) => sum + (r.currency === 'USD' ? 0 : Number(r.amount) || 0), 0);
  const totalUsd = rows.reduce((sum, r) => sum + (r.currency === 'USD' ? Number(r.amount) || 0 : 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          Total advanced: <strong style={{ color: 'var(--text)' }}>CDN {total.toFixed(2)}</strong>
          {totalUsd > 0 && <> &nbsp;/&nbsp; <strong style={{ color: 'var(--text)' }}>USD {totalUsd.toFixed(2)}</strong></>}
        </div>
        <button type="button" className="primary-btn" onClick={() => setModalOpen(true)}>+ Add Entry</button>
      </div>

      {rows.length === 0 ? <div className="empty">No advance payments yet.</div> : (
        <table>
          <thead><tr><th>Date</th><th>Amount</th><th>Currency</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.entry_date || '—'}</td>
                <td>{r.amount != null ? Number(r.amount).toFixed(2) : '—'}</td>
                <td>{r.currency || '—'}</td>
                <td>{r.description || '—'}</td>
                <td><button type="button" onClick={() => onDelete(r.id)} style={{ color: 'var(--red)' }}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <AdvancePaymentModal driverId={driverId} onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); onSaved(); }} />
      )}
    </div>
  );
}

function AdvancePaymentModal({ driverId, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('CDN');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (!amount) { setError('Amount is required.'); return; }
    setSaving(true);
    try {
      await api(`/dispatch/drivers/${driverId}/documents`, {
        method: 'POST', body: { category: 'advance_payment', amount, currency, entryDate: date, description: notes }
      });
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ minWidth: 380 }} onClick={e => e.stopPropagation()}>
        <h3>Add advance payment</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
          <label>Amount<br/><input type="number" value={amount} onChange={e => setAmount(e.target.value)} /></label>
          <label>Currency<br/>
            <select value={currency} onChange={e => setCurrency(e.target.value)}>
              <option value="CDN">CDN</option>
              <option value="USD">USD</option>
            </select>
          </label>
        </div>
        <label>Date<br/><input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ marginBottom: 12 }} /></label>
        <label>Notes<br/><input value={notes} onChange={e => setNotes(e.target.value)} /></label>
        {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '12px 0 0' }}>{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="button" className="primary-btn" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function TicketsAccidentsForm({ driverId, rows, onSaved, onDelete }) {
  const [ticketType, setTicketType] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [fineAmount, setFineAmount] = useState('');
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);

  const add = async () => {
    if (!ticketType && !notes.trim()) return;
    await api(`/dispatch/drivers/${driverId}/documents`, {
      method: 'POST',
      body: {
        category: 'tickets_accidents', ticketType, entryDate: date, location, caseNumber,
        fineAmount, status, description: notes,
        fileName: uploadedFile?.fileName || '', fileUrl: uploadedFile?.url || ''
      }
    });
    setTicketType(''); setDate(''); setLocation(''); setCaseNumber(''); setFineAmount('');
    setStatus(''); setNotes(''); setUploadedFile(null);
    onSaved();
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <label>Type<br/>
          <ManagedDropdown field="ticket_type" value={ticketType} onChange={setTicketType} />
        </label>
        <label>Date<br/><input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
        <label>Location<br/><input value={location} onChange={e => setLocation(e.target.value)} /></label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <label>Case / Ticket #<br/><input value={caseNumber} onChange={e => setCaseNumber(e.target.value)} /></label>
        <label>Fine Amount<br/><input type="number" value={fineAmount} onChange={e => setFineAmount(e.target.value)} /></label>
        <label>Status<br/>
          <ManagedDropdown field="ticket_status" value={status} onChange={setStatus} />
        </label>
      </div>
      <label style={{ display: 'block', marginBottom: 14 }}>Notes<br/><input value={notes} onChange={e => setNotes(e.target.value)} /></label>
      <div style={{ marginBottom: 18 }}>
        <FileThumbPicker value={uploadedFile} onChange={setUploadedFile} />
      </div>
      <button type="button" className="primary-btn" onClick={add} style={{ marginBottom: 18 }}>+ Add</button>

      {rows.length === 0 ? <div className="empty">No entries yet.</div> : (
        <table>
          <thead><tr><th>Date</th><th>Type</th><th>Location</th><th>Case #</th><th>Fine</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.entry_date || '—'}</td>
                <td>{r.ticket_type || '—'}</td>
                <td>{r.location || '—'}</td>
                <td>{r.case_number || '—'}</td>
                <td>{r.fine_amount != null ? Number(r.fine_amount).toFixed(2) : '—'}</td>
                <td>{r.status || '—'}</td>
                <td><button type="button" onClick={() => onDelete(r.id)} style={{ color: 'var(--red)' }}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
